import { DecimalPipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { CURRENT_STATE_PROJECT_KEY, GraphData } from '../common/linqset-model';
import { AIModelChange } from '../common/model-changes';
import { Organisation, Project, PROJECT_NAME_MAX } from '../common/organisation';
import { USER_ROLE } from '../common/user';
import { EpmAppliedChange } from '../epm/epm-future-state';
import { ScenarioService } from './scenario.service';
import { compareScenarios, organisationCost, ScenarioComparison, ScenarioMetrics, scenarioMetrics } from './scenario-comparison';
import { SCENARIO_DAYS, SCENARIO_SECONDS_PER_TICK, ScenarioRun } from './scenario.service';
import { buildSimulationReport, formatWorkingTime } from './simulation-report';
import { SimulationRun } from './simulation-run';
import { applyWhatIf, DEFAULT_WHAT_IF, detourShare, fullTimePeople, inputFactor, RoleRequirement, roleRequirements, settingsWithNeededPeople, WhatIfData, WhatIfDetour, WhatIfInput, WhatIfOptions, whatIfOptions, WhatIfSettings } from './whatif-model';

export interface WhatIfDialogData {
    organisation: Organisation;
    analysis: { nodes: AnalysisNode[]; edges: AnalysisEdge[] };     // the workspace's analysis data
    modelKeys: string[];                                            // the models looked at
    organisationModel: GraphData;
    workspaceKey: string;
    formatCost: (value: number) => string;
}

const METRICS: { key: keyof ScenarioMetrics; label: string; lowerIsBetter: boolean; format: 'number' | 'time' | 'cost' }[] = [
    { key: 'produced', label: 'Outputs produced', lowerIsBetter: false, format: 'number' },
    { key: 'waiting', label: 'Items waiting at steps', lowerIsBetter: true, format: 'number' },
    { key: 'delaySeconds', label: 'Average wait on the way to an output', lowerIsBetter: true, format: 'time' },
    { key: 'behind', label: "Steps that can't keep up", lowerIsBetter: true, format: 'number' },
    { key: 'cost', label: 'Annual cost of people and systems', lowerIsBetter: true, format: 'cost' },
];

const MAX_INPUTS = 12;
const DAY_CHOICES = [5, 10, 20];

// What if the load changed, or the people on a role? The user sets the items a year put in and the people on each
// role, sees at once what the roles would need to meet the load, and simulates it against today to see where work
// would queue. Nothing is changed in the model: the analysis data is adjusted in the browser and simulated there
@Component({
    selector: 'app-whatif-dialog',
    standalone: true,
    imports: [DecimalPipe, FormsModule, MatButtonModule, MatDialogModule, MatIconModule, MatProgressBarModule, MatTooltipModule],
    template: `
        <h2 mat-dialog-title>What if: load and capacity</h2>
        <mat-dialog-content>
            <p class="intro">Change the load put into the models, or the people on a role, and see what it takes to keep up. The roles' needs are worked out at
                once from their hours and capacity; the simulation then shows where work would queue, against today. Nothing in the model is changed.</p>

            <!-- the load -->
            <section class="block" aria-labelledby="whatif-load">
                <div class="head">
                    <h4 id="whatif-load">Load</h4>
                    <span class="muted">{{ totalItemsToday | number }} items a year today · <b>{{ totalItems | number }}</b> in the what-if</span>
                </div>
                <label class="slider">
                    <span>Overall load <b>{{ loadPercent }}%</b> of today's</span>
                    <input type="range" min="25" max="300" step="5" [ngModel]="loadPercent" (ngModelChange)="setLoad($event)" data-testid="whatif-load">
                </label>
                @if (options.inputs.length) {
                <table class="list" data-testid="whatif-inputs">
                    <thead><tr><th>Input</th><th class="optional">Model</th><th class="num">Items a year today</th><th class="num">In the what-if</th><th></th></tr></thead>
                    <tbody>
                        @for (i of shownInputs; track i.key) {
                        <tr>
                            <td>{{ i.name }}</td>
                            <td class="optional muted">{{ i.model }}</td>
                            <td class="num">{{ i.itemsPerYear | number }}</td>
                            <td class="num"><input type="number" min="0" class="cell" [ngModel]="itemsOf(i)" (ngModelChange)="setItems(i, $event)" [attr.aria-label]="'Items a year at ' + i.name" [attr.data-testid]="'whatif-input-' + i.key"></td>
                            <td>@if (i.key in settings.inputItems) {<button type="button" class="link" (click)="followLoad(i)">Follow the overall load</button>}</td>
                        </tr>
                        }
                    </tbody>
                </table>
                @if (options.inputs.length > shownInputs.length) {<p class="muted small">{{ options.inputs.length - shownInputs.length }} smaller inputs follow the overall load.</p>}
                }
            </section>

            <!-- the detours -->
            @if (options.detours.length) {
            <section class="block" aria-labelledby="whatif-detours">
                <div class="head">
                    <h4 id="whatif-detours">Detours from the main flow</h4>
                    <span class="muted">{{ data.formatCost(exceptionCostToday) }} of exception work a year today · <b>{{ data.formatCost(exceptionCost) }}</b> in the what-if</span>
                </div>
                <p class="muted small">Where items leave the main flow and return later, such as collecting what an input is missing. Cut the share of items taking a detour,
                    as fixing the data at the source would, and the rest of its items carry on along the main flow.</p>
                <table class="list" data-testid="whatif-detours">
                    <thead><tr><th>Items leave at</th><th class="optional">Through</th><th class="optional">Return at</th><th class="num">Share today</th><th class="num">In the what-if</th><th class="num">Items a year</th><th class="num">Exception work</th><th></th></tr></thead>
                    <tbody>
                        @for (d of options.detours; track d.key) {
                        <tr [class.short]="detourShareOf(d) < d.share" [class.spare]="detourShareOf(d) > d.share">
                            <td>{{ d.leavesAt }}</td>
                            <td class="optional muted">{{ d.steps.join(', ') }}</td>
                            <td class="optional muted">{{ d.returnsAt }}</td>
                            <td class="num">{{ d.share * 100 | number: '1.0-1' }}%</td>
                            <td class="num"><input type="number" min="0" max="100" step="0.5" class="cell" [ngModel]="detourShareOf(d) * 100 | number: '1.0-1'" (ngModelChange)="setDetourShare(d, $event)" [attr.aria-label]="'Share of items taking the detour from ' + d.leavesAt" [attr.data-testid]="'whatif-detour-' + d.key">%</td>
                            <td class="num">{{ count(d.splitItems * detourShareOf(d) * inputFactorOfSplit(d)) }}</td>
                            <td class="num">{{ data.formatCost(d.cost * detourRatio(d)) }}<span class="muted small"> of {{ data.formatCost(d.cost) }}</span></td>
                            <td>@if (d.key in settings.detourShares) {<button type="button" class="link" (click)="resetDetour(d)">Today's share</button>}</td>
                        </tr>
                        }
                    </tbody>
                </table>
            </section>
            }

            <!-- the capacity -->
            <section class="block" aria-labelledby="whatif-people">
                <div class="head">
                    <h4 id="whatif-people">Capacity</h4>
                    @if (anyNeeded) {
                    <button mat-stroked-button color="accent" (click)="useNeeded()" data-testid="whatif-use-needed" matTooltip="Set each role's people to what the adjusted load needs">
                        <mat-icon>group_add</mat-icon>Set people to what's needed</button>
                    }
                </div>
                @if (requirements.length) {
                <table class="list" data-testid="whatif-roles">
                    <thead><tr><th>Role</th><th class="num">People today</th><th class="num">In the what-if</th><th class="num">Work a year</th><th class="num">Of capacity</th><th class="num">Needed</th><th class="num">Cost a year</th></tr></thead>
                    <tbody>
                        @for (r of requirements; track r.key) {
                        <tr [class.short]="r.extra > 0" [class.spare]="r.extra < 0">
                            <td>{{ r.name }}</td>
                            <td class="num">{{ r.peopleToday || 'not set' }}</td>
                            <td class="num">
                                @if (r.peopleToday > 0) {
                                <input type="number" min="0" step="1" class="cell" [ngModel]="r.people" (ngModelChange)="setPeople(r, $event)" [attr.aria-label]="'People on ' + r.name" [attr.data-testid]="'whatif-people-' + r.key">
                                } @else {<span class="muted" matTooltip="Set the people on the role in the organisation model to change its capacity here">–</span>}
                            </td>
                            <td class="num" [matTooltip]="(r.hoursToday | number: '1.0-0') + ' hours today'">{{ r.hours | number: '1.0-0' }} h</td>
                            <td class="num" [class.over]="r.utilisation !== null && r.utilisation > 1">{{ r.utilisation === null ? '–' : (r.utilisation * 100 | number: '1.0-0') + '%' }}</td>
                            <td class="num">
                                @if (r.needed === null) {<span class="muted">–</span>}
                                @else {{{ r.needed }} <span class="extra" [class.more]="r.extra > 0" [class.fewer]="r.extra < 0">{{ r.extra > 0 ? '+' + r.extra : r.extra < 0 ? r.extra : 'met' }}</span>}
                            </td>
                            <td class="num">{{ data.formatCost(r.cost) }}</td>
                        </tr>
                        }
                    </tbody>
                </table>
                <p class="muted small">Needed is the people the adjusted work takes at the capacity each person has today ({{ fte(adjustedHours) }} full-time people of work in all).
                    The simulation below also finds where more people are needed at once, when work arrives in bursts.</p>
                } @else {
                <p class="muted">No role with people performs the steps looked at.</p>
                }
            </section>

            <!-- the simulation -->
            <section class="block" aria-labelledby="whatif-sim">
                <div class="head">
                    <h4 id="whatif-sim">Simulation</h4>
                    <span class="days">
                        <span class="muted">Simulate</span>
                        @for (d of dayChoices; track d) {<button type="button" class="chip" [class.active]="days === d" (click)="days = d" [disabled]="stage === 'working'">{{ d }} days</button>}
                    </span>
                    <button mat-flat-button color="primary" (click)="simulate()" [disabled]="stage === 'working'" data-testid="whatif-simulate"><mat-icon>play_arrow</mat-icon>Simulate</button>
                </div>
                @switch (stage) {
                @case ('working') {
                <p role="status" data-testid="whatif-progress">{{ stepLabel }}…</p>
                <mat-progress-bar mode="determinate" [value]="share * 100"></mat-progress-bar>
                }
                @case ('error') {
                <p class="error" role="alert">{{ error }}</p>
                }
                @case ('results') {
                @if (result; as r) {
                <p class="verdict" [class]="'verdict ' + r.comparison.verdict" data-testid="whatif-verdict">{{ r.comparison.summary }}</p>
                <table class="list" data-testid="whatif-metrics">
                    <thead><tr><th>Over {{ r.days }} working days</th><th class="num">Today</th><th class="num">What if</th><th class="num">Change</th></tr></thead>
                    <tbody>
                        @for (m of metrics; track m.key) {
                        <tr>
                            <td>{{ m.label }}</td>
                            <td class="num">{{ show(m.format, r.comparison.current[m.key]) }}</td>
                            <td class="num">{{ show(m.format, r.comparison.draft[m.key]) }}</td>
                            <td class="num" [class.better]="isBetter(m, r.comparison)" [class.worse]="isWorse(m, r.comparison)">{{ difference(m, r.comparison) }}</td>
                        </tr>
                        }
                    </tbody>
                </table>
                @if (r.run.report.roles.length) {
                <h5>What the simulation says the roles need</h5>
                <ul class="needs" data-testid="whatif-sim-roles">
                    @for (n of r.run.report.roles; track n.key) {
                    <li><b>{{ n.name }}</b>: {{ n.extraHours > 0 ? (n.extraHours | number) + ' more hours a year (' + fte(n.extraHours) + ' full-time)' : '' }}{{ n.extraHours > 0 && n.extraPeople > 0 ? ' and ' : '' }}{{ n.extraPeople > 0 ? n.extraPeople + ' more at once when work arrives together' : '' }}, holding up {{ names(n.steps) }}.</li>
                    }
                </ul>
                }
                @if (r.comparison.steps.length) {
                <h5>Where work waited</h5>
                <table class="list" data-testid="whatif-steps">
                    <thead><tr><th>Step</th><th class="num">Today</th><th class="num">What if</th></tr></thead>
                    <tbody>
                        @for (s of r.comparison.steps.slice(0, 12); track s.key) {
                        <tr>
                            <td>{{ s.name }}<span class="muted small block">{{ s.model }}</span></td>
                            <td class="num">{{ wait(s.current.waitSeconds, s.current.queue) }}</td>
                            <td class="num" [class.better]="s.better" [class.worse]="!s.better && s.draft && s.draft.waitSeconds > s.current.waitSeconds">{{ s.draft ? wait(s.draft.waitSeconds, s.draft.queue) : 'no longer a step' }}</td>
                        </tr>
                        }
                    </tbody>
                </table>
                }
                @if (stale) {<p class="muted small">The settings have changed since this ran. Simulate again to see them.</p>}
                }
                }
                @default {
                <p class="muted small">Runs today's models and the what-if for the days chosen and compares them: outputs produced, work waiting, and the steps and roles that can't keep up.</p>
                }
                }
            </section>

            <!-- keeping it -->
            @if (canKeep) {
            <section class="block" aria-labelledby="whatif-keep">
                <div class="head">
                    <h4 id="whatif-keep">Keep as a project</h4>
                </div>
                @switch (keepStage) {
                @case ('working') {
                <p role="status" data-testid="whatif-keep-progress">{{ keepStepLabel }}…</p>
                <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                }
                @case ('done') {
                @if (kept; as k) {
                <p class="verdict better" data-testid="whatif-kept">Kept as the project “{{ k.project.name }}”. Compare it on the EPM scorecard, or open it to change it further.</p>
                <ul class="needs">
                    @for (c of k.applied; track $index) {<li [class.muted]="!c.applied">{{ c.applied ? '' : 'Not made: ' }}{{ c.description }}@if (!c.applied && c.reason) { ({{ c.reason }})}</li>}
                </ul>
                }
                }
                @default {
                <p class="muted small">Makes these settings in a new project, so it can be scored by LINQ and compared with current state on the EPM scorecard:</p>
                <ul class="needs" data-testid="whatif-changes">
                    @for (c of projectChanges; track $index) {<li>{{ c.description }}</li>}
                </ul>
                <div class="keep-row">
                    <input type="text" class="cell name" [(ngModel)]="keepName" placeholder="Project name" [attr.maxlength]="nameMax" aria-label="Project name" data-testid="whatif-keep-name">
                    <button mat-flat-button color="primary" (click)="keepAsProject()" [disabled]="!keepName.trim() || !projectChanges.length || stage === 'working'" data-testid="whatif-keep"><mat-icon>save</mat-icon>Keep as a project</button>
                </div>
                @if (keepError) {<p class="error" role="alert">{{ keepError }}</p>}
                }
                }
            </section>
            }
        </mat-dialog-content>
        <mat-dialog-actions align="end">
            <button mat-button (click)="reset()" [disabled]="stage === 'working' || keepStage === 'working'">Back to today</button>
            <button mat-flat-button color="primary" mat-dialog-close [disabled]="stage === 'working' || keepStage === 'working'">Close</button>
        </mat-dialog-actions>
    `,
    styles: [`
        .intro { margin: 0 0 8px; color: var(--slate, #5b6172); font-size: 13px; line-height: 1.5; }
        .block { margin-top: 14px; padding-top: 10px; border-top: 1px solid rgba(0,0,0,.08); }
        .head { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 6px; }
        h4 { margin: 0; font-size: 14px; }
        h5 { margin: 12px 0 4px; font-size: 12.5px; color: var(--slate, #5b6172); text-transform: uppercase; letter-spacing: .3px; }
        .muted { color: var(--slate, #5b6172); }
        .small { font-size: 12px; }
        .block.small, p.small { margin: 6px 0 0; }
        .slider { display: flex; flex-direction: column; gap: 4px; margin: 4px 0 8px; font-size: 13px; }
        .slider input { width: 100%; accent-color: var(--teal, #00a9a5); }
        .list { width: 100%; border-collapse: collapse; }
        .list th { text-align: left; font-weight: 600; font-size: 12px; color: var(--slate, #5b6172); padding: 2px 6px; white-space: nowrap; }
        .list td { padding: 4px 6px; border-top: 1px solid rgba(0,0,0,.08); vertical-align: middle; font-size: 13px; }
        .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .cell { width: 90px; padding: 3px 6px; border: 1px solid rgba(0,0,0,.2); border-radius: 6px; font: inherit; text-align: right; }
        .link { padding: 0; border: 0; background: none; font: inherit; font-size: 12px; color: #00706d; cursor: pointer; }
        .link:hover { text-decoration: underline; }
        tr.short td { background: #fff7ed; }
        tr.spare td { background: #f0fdf4; }
        .over { color: #b91c1c; font-weight: 600; }
        .extra { margin-left: 4px; font-size: 11.5px; color: var(--slate, #5b6172); }
        .extra.more { color: #b91c1c; font-weight: 600; }
        .extra.fewer { color: #15803d; }
        .days { display: flex; align-items: center; gap: 6px; }
        .chip { padding: 2px 10px; border: 1px solid rgba(0,0,0,.15); border-radius: 12px; background: #f4f5f9; font: inherit; font-size: 12px; cursor: pointer; }
        .chip.active { border-color: #9fd8d5; background: #e0f5f4; color: #00706d; font-weight: 600; }
        .better { color: #15803d; font-weight: 600; }
        .worse { color: #b91c1c; font-weight: 600; }
        .verdict { padding: 8px 12px; border-radius: 8px; font-weight: 500; }
        .verdict.better { background: #dcfce7; color: #166534; }
        .verdict.mixed { background: #fef3c7; color: #92400e; }
        .verdict.worse { background: #fee2e2; color: #991b1b; }
        .verdict.same { background: #e5e7eb; color: #374151; }
        .needs { margin: 0; padding-left: 18px; font-size: 13px; }
        .keep-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 8px; }
        .cell.name { width: 320px; max-width: 100%; text-align: left; }
        .block.needs li { margin: 2px 0; }
        .error { color: #b91c1c; }
        @media (max-width: 700px) { .optional { display: none; } }
    `],
})
export class WhatIfDialogComponent {
    readonly metrics = METRICS;
    readonly dayChoices = DAY_CHOICES;

    options: WhatIfOptions;
    settings: WhatIfSettings = { ...DEFAULT_WHAT_IF, inputItems: {}, people: {}, detourShares: {} };
    adjusted!: WhatIfData;
    requirements: RoleRequirement[] = [];
    days = SCENARIO_DAYS;

    stage: 'set' | 'working' | 'error' | 'results' = 'set';
    stepLabel = '';
    share = 0;
    error = '';
    result?: { run: ScenarioRun; comparison: ScenarioComparison; days: number };
    stale = false;

    private readonly modelKeys: Set<string>;
    private readonly modelNames: Map<string, string>;
    private readonly costToday: number;
    private baseline?: { run: ScenarioRun; days: number };
    private request = 0;

    // keeping the what-if as a project
    readonly nameMax = PROJECT_NAME_MAX;
    keepName = '';
    keepStage: 'idle' | 'working' | 'done' = 'idle';
    keepStepLabel = '';
    keepError = '';
    kept?: { project: Project; applied: EpmAppliedChange[] };

    constructor(@Inject(MAT_DIALOG_DATA) public data: WhatIfDialogData, private dialogRef: MatDialogRef<WhatIfDialogComponent>, private scenarios: ScenarioService) {
        this.modelKeys = new Set(data.modelKeys);
        this.modelNames = new Map((data.organisation.models || []).map(m => [m._key, m.name]));
        this.costToday = organisationCost(data.organisationModel, data.workspaceKey);
        this.options = whatIfOptions(data.analysis, this.modelKeys, data.organisationModel, data.workspaceKey, Object.fromEntries(this.modelNames));
        this.refresh();
    }

    // the adjusted data and what the roles need, for the settings as they stand
    private refresh() {
        this.adjusted = applyWhatIf(this.data.analysis, this.modelKeys, this.options, this.settings, this.costToday);
        this.requirements = roleRequirements(this.options, this.adjusted);
        if (this.result) this.stale = true;
    }

    get loadPercent() {
        return Math.round(this.settings.loadFactor*100);
    }

    setLoad(percent: number) {
        this.settings = { ...this.settings, loadFactor: Math.max(0.01, (Number(percent) || 100)/100) };
        this.refresh();
    }

    get shownInputs(): WhatIfInput[] {
        return this.options.inputs.slice(0, MAX_INPUTS);
    }

    itemsOf(input: WhatIfInput) {
        return Math.round(input.itemsPerYear*inputFactor(input, this.settings));
    }

    setItems(input: WhatIfInput, items: number) {
        const value = Math.max(0, Number(items) || 0);
        this.settings = { ...this.settings, inputItems: { ...this.settings.inputItems, [input.key]: value } };
        this.refresh();
    }

    followLoad(input: WhatIfInput) {
        const { [input.key]: _, ...rest } = this.settings.inputItems;
        this.settings = { ...this.settings, inputItems: rest };
        this.refresh();
    }

    get totalItemsToday() {
        return this.options.inputs.reduce((sum, i) => sum + i.itemsPerYear, 0);
    }

    get totalItems() {
        return this.options.inputs.reduce((sum, i) => sum + i.itemsPerYear*inputFactor(i, this.settings), 0);
    }

    setPeople(requirement: RoleRequirement, people: number) {
        const value = Math.max(0, Math.round(Number(people) || 0));
        this.settings = { ...this.settings, people: { ...this.settings.people, [requirement.key]: value } };
        this.refresh();
    }

    get anyNeeded() {
        return this.requirements.some(r => r.needed !== null && r.extra !== 0);
    }

    useNeeded() {
        this.settings = settingsWithNeededPeople(this.settings, this.requirements);
        this.refresh();
    }

    get adjustedHours() {
        return this.requirements.reduce((sum, r) => sum + r.hours, 0);
    }

    fte(hours: number) {
        return fullTimePeople(hours);
    }

    count(value: number) {
        return Math.round(value).toLocaleString();
    }

    reset() {
        this.settings = { ...DEFAULT_WHAT_IF, inputItems: {}, people: {}, detourShares: {} };
        this.refresh();
    }

    // ---------- keeping it as a project ----------

    // projects are made from the current state, by those who can add them
    get canKeep(): boolean {
        return this.data.workspaceKey === CURRENT_STATE_PROJECT_KEY && (this.data.organisation.role || USER_ROLE.READ) <= USER_ROLE.WRITE;
    }

    // the what-if's settings as changes to the model: the load at each input, the share of items on each detour, and
    // the people on each role, where they differ from today
    get projectChanges(): AIModelChange[] {
        const change = (c: Partial<AIModelChange> & Pick<AIModelChange, 'kind' | 'description'>): AIModelChange =>
            ({ action: null, model: null, role: null, newRole: null, system: null, newSystem: null, percent: null, people: null, cost: null, from: null, items: null, output: null, minutes: null, ...c });
        const changes: AIModelChange[] = [];
        this.options.inputs.forEach(i => {
            const items = Math.round(i.itemsPerYear*inputFactor(i, this.settings));
            if (items !== Math.round(i.itemsPerYear)) changes.push(change({ kind: 'volume', action: i.name, model: i.model, items, description: `${i.name} does ${items.toLocaleString()} items a year rather than ${Math.round(i.itemsPerYear).toLocaleString()}` }));
        });
        this.options.detours.forEach(d => {
            const share = this.detourShareOf(d);
            if (share !== d.share) changes.push(change({ kind: 'share', from: d.leavesAt, action: d.firstStep, model: d.model, percent: Math.round(share*1000)/10,
                description: `${Math.round(share*1000)/10}% of the items leaving ${d.leavesAt} go to ${d.firstStep} rather than ${Math.round(d.share*1000)/10}%` }));
        });
        this.options.roles.forEach(r => {
            const people = this.settings.people[r.key];
            if (r.people > 0 && people !== undefined && people !== r.people) changes.push(change({ kind: 'staff', role: r.name, people, description: `${r.name} has ${people} ${people === 1 ? 'person' : 'people'} rather than ${r.people}` }));
        });
        return changes;
    }

    async keepAsProject() {
        const name = this.keepName.trim().slice(0, PROJECT_NAME_MAX);
        const changes = this.projectChanges;
        if (!name || !changes.length || this.keepStage === 'working') return;
        this.keepStage = 'working';
        this.keepError = '';
        this.dialogRef.disableClose = true;
        try {
            const labels = { creating: 'Creating the project', applying: 'Making the changes in it', keeping: 'Keeping it' };
            this.kept = await this.scenarios.keepWhatIf(this.data.organisation, changes, name, step => this.keepStepLabel = labels[step]);
            this.keepStage = 'done';
        } catch (err: any) {
            console.error('Unable to keep the what-if as a project', err);
            this.keepError = err?.message || "The project couldn't be made. Please try again.";
            this.keepStage = 'idle';
        } finally {
            this.dialogRef.disableClose = false;
        }
    }

    // ---------- detours ----------

    detourShareOf(d: WhatIfDetour) {
        return detourShare(d, this.settings);
    }

    // the detour's exception work in the what-if as a multiple of today's: its share, at the load reaching its split
    detourRatio(d: WhatIfDetour) {
        return d.share > 0 ? (this.detourShareOf(d)/d.share)*this.inputFactorOfSplit(d) : 1;
    }

    // the load reaching the detour's split in the what-if, as a multiple of today's
    inputFactorOfSplit(d: WhatIfDetour) {
        const today = applyWhatIf(this.data.analysis, this.modelKeys, this.options, { ...this.settings, detourShares: {} }, this.costToday);
        return today.factors.get(d.key.split('>')[0]) ?? this.settings.loadFactor;
    }

    setDetourShare(d: WhatIfDetour, percent: number | string) {
        const value = Math.max(0, Math.min(100, Number(percent) || 0))/100;
        this.settings = { ...this.settings, detourShares: { ...this.settings.detourShares, [d.key]: value } };
        this.refresh();
    }

    resetDetour(d: WhatIfDetour) {
        const { [d.key]: _, ...rest } = this.settings.detourShares;
        this.settings = { ...this.settings, detourShares: rest };
        this.refresh();
    }

    get exceptionCostToday() {
        return this.options.detours.reduce((sum, d) => sum + d.cost, 0);
    }

    get exceptionCost() {
        return this.options.detours.reduce((sum, d) => sum + d.cost*this.detourRatio(d), 0);
    }

    // simulates today's models (once for the days chosen) and the what-if, and compares them
    async simulate() {
        if (this.stage === 'working') return;
        const request = ++this.request;
        this.stage = 'working';
        this.error = '';
        this.dialogRef.disableClose = true;
        try {
            const days = this.days;
            if (!this.baseline || this.baseline.days !== days) {
                this.stepLabel = 'Simulating today';
                const today = applyWhatIf(this.data.analysis, this.modelKeys, this.options, DEFAULT_WHAT_IF, this.costToday);
                const run = await this.run(today, days, share => this.share = share/2);
                if (request !== this.request) return;
                this.baseline = { run, days };
            }
            this.stepLabel = 'Simulating the what-if';
            const run = await this.run(this.adjusted, days, share => this.share = 0.5 + share/2);
            if (request !== this.request) return;
            this.result = { run, comparison: compareScenarios(this.baseline.run, run, this.data.formatCost), days };
            this.stale = false;
            this.stage = 'results';
        } catch (err: any) {
            if (request !== this.request) return;
            console.error('Unable to simulate the what-if', err);
            this.error = err?.message || "The what-if couldn't be simulated. Please try again.";
            this.stage = 'error';
        } finally {
            if (request === this.request) this.dialogRef.disableClose = false;
        }
    }

    private async run(adjusted: WhatIfData, days: number, progress: (share: number) => void): Promise<ScenarioRun> {
        const run = SimulationRun.fromAnalysis(adjusted.data, this.modelKeys, SCENARIO_SECONDS_PER_TICK);
        await run.runFor(days, progress);
        const snapshot = run.snapshot();
        const report = buildSimulationReport(snapshot, adjusted.roles, this.modelNames);
        return { snapshot, report, metrics: scenarioMetrics(snapshot, report, adjusted.cost) };
    }

    names(steps: { name: string }[]) {
        const n = steps.slice(0, 3).map(s => `“${s.name}”`);
        return n.length <= 1 ? n.join('') : `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
    }

    show(format: 'number' | 'time' | 'cost', value: number) {
        if (format === 'time') return value ? formatWorkingTime(value) : '–';
        if (format === 'cost') return this.data.formatCost(value);
        return Math.round(value).toLocaleString();
    }

    wait(seconds: number, queue: number) {
        return queue === 0 ? 'none' : `${formatWorkingTime(seconds)} (${queue.toLocaleString()} waiting)`;
    }

    private delta(m: typeof METRICS[number], c: ScenarioComparison) {
        return c.draft[m.key] - c.current[m.key];
    }

    isBetter(m: typeof METRICS[number], c: ScenarioComparison) {
        const d = this.delta(m, c);
        return m.lowerIsBetter ? d < 0 : d > 0;
    }

    isWorse(m: typeof METRICS[number], c: ScenarioComparison) {
        const d = this.delta(m, c);
        return m.lowerIsBetter ? d > 0 : d < 0;
    }

    difference(m: typeof METRICS[number], c: ScenarioComparison) {
        const d = this.delta(m, c);
        if (!d) return '–';
        const sign = d > 0 ? '+' : '−';
        if (m.format === 'time') return `${sign}${formatWorkingTime(Math.abs(d))}`;
        if (m.format === 'cost') return `${sign}${this.data.formatCost(Math.abs(d))}`;
        const current = c.current[m.key];
        return `${sign}${Math.round(Math.abs(d)).toLocaleString()}${current ? ` (${Math.round(Math.abs(d)/current*100)}%)` : ''}`;
    }
}
